// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Bom manager class.
 * @author James Reid
 */

// @ts-check

// @@imports-package
import { Product, Membrane, Trim, Corner, Tape, Adhesive } from "../products/index.js"

// @@imports-utils
import { DecoratedError } from "../utils/index.js"

// @@imports-types
import { LineItem, RawLineItem } from "../types/LineItem.js"
import { ADHESIVE_TYPES, AdhesiveType, EDGE_TYPES, EdgeType, MEMBRANE_TYPES, MembraneType, PRODUCT_DISPLAY_CATEGORIES, ProductDisplayCategory, TAPE_TYPES, TapeType } from "../types/Enums.js"
import { CategorizedBom } from "../../web/scripts/types/CategorizedBom.js"

// @@body
class Bom {
    #uniqueLineItems = /** @type {Map.<string, LineItem>} */ (new Map())
    #nonUniqueLineItems = /** @type {LineItem[]} */ ([])

    /**
     * Instantiate bill of materials.
     * @param {object} obj Optional destructured parameters object.
     * @param {number} [obj.vatRate] Decimal VAT rate to apply to line items.
     * @param {boolean} [obj.allowNegativeQuantities] Should negative quantities
     * be allowed on a line item?
     */
    constructor({ vatRate = 0.2, allowNegativeQuantities = false } = {}) {
        this.vatRate = vatRate
        this.allowNegativeQuantities = allowNegativeQuantities
    }

    /**
     * Add line item to existing bom instance or update the quantity of an
     * existing line item if a matching product is found.
     * @param {Product} product Product to add to bom.
     * @param {number} deltaQuantity Quantity of product to add to bom.
     * @param {object} obj Optional destructured parameters object.
     * @param {boolean} [obj.forcePositiveQuantity] Should updated quantity be
     * clamped to a positive value?
     * @param {boolean} [obj.forceUnique] Should line item be unique?
     * @param {object} [obj.detail] Optional details object relating to line
     * item for feature locations (membrane zones, drain placement etc.).
     * @returns {Bom} Current bom instance.
     */
    addLineItem(product, deltaQuantity, {
        forcePositiveQuantity = false,
        forceUnique = false,
        detail
    } = {}) {
        // Look for product match in non unique line items with the *exact*
        // reverse quantity (positive <-> negative) of the quantity change. If
        // found, remove the non unique line item found and return.
        const nonUniqueIndex = this.#nonUniqueLineItems.findIndex(lineItem => {
            return lineItem.product === product &&
                lineItem.quantity === - deltaQuantity
        })
        if (nonUniqueIndex >= 0) {
            this.#nonUniqueLineItems.splice(nonUniqueIndex, 1)
            return this
        }

        // Fetch existing line item if found in unique line items map.
        const { uuid, sku, code } = product
        let lineItem = this.#uniqueLineItems.get(code)

        // Infer line item quantity from existing matching line item and prevent
        // negative values where required.
        let quantity = lineItem?.quantity || 0
        quantity += deltaQuantity
        if (!this.allowNegativeQuantities || forcePositiveQuantity) {
            quantity = Math.max(quantity, 0)
        }

        // Calculate total line price and VAT price.
        const price = parseFloat((product.price * quantity).toFixed(2))
        const vatPrice = parseFloat(
            (product.price * quantity * (1 + this.vatRate)).toFixed(2)
        )

        // Update existing unique line item if available otherwise build new
        // line item object and save according to product settings and force
        // unique flag.
        if (lineItem) {
            Object.assign(lineItem, { quantity, price, vatPrice, detail })
        }
        else {
            lineItem = {
                uuid, sku, code, quantity, product, price, vatPrice, detail
            }
            product.isUniqueLineItem || forceUnique
                ? this.#uniqueLineItems.set(code, lineItem)
                : this.#nonUniqueLineItems.push(lineItem)
        }

        return this
    }

    /**
     * Remove all line items with a given product from current bom instance
     * across unique and non-unique line items.
     * @param {Product} product Product to remove from bom.
     * @returns {Bom} Current bom instance.
     */
    removeLineItem(product) {
        const { code } = product

        // Remove line item by calculator code.
        this.#uniqueLineItems.delete(code)
        this.#nonUniqueLineItems = this.#nonUniqueLineItems.filter(lineItem => {
            return lineItem.code !== code ? true : false
        })

        return this
    }

    /**
     * Remove *all* line items of *all* products, effectively resetting bom
     * instance.
     * @returns {Bom} Current (reset) bom instance with no line items.
     */
    clearLineItems() {
        // Remove *all* line items.
        for (const { product } of this.lineItems) {
            this.removeLineItem(product)
        }

        return this
    }

    /**
     * Get total quantity of a given product in current bom instance across
     * unique and non-unique line items.
     * @param {Product} product Product to search for in bom.
     * @returns {number} Total quantity of line item in current bom instance.
     */
    getLineItemQuantity(product) {
        const { code } = product
        const mapQuantity = this.#uniqueLineItems.get(code)?.quantity

        return mapQuantity || this.#nonUniqueLineItems.reduce((acc, cur) => {
            return cur.code === code ? acc + cur.quantity : acc
        }, 0)
    }

    /**
     * Update line item product quantity in current bom instance.
     * @param {Product} product Product for which to set quantity in bom.
     * @param {number} quantity Target quantity of product in bom.
     * @returns {Bom} Current bom instance.
     * @throws {BomError} Throws when:
     * - Attempting to set quantity of a non-unique line item.
     */
    setLineItemQuantity(product, quantity) {
        // Prevent negative values where required.
        if (!this.allowNegativeQuantities) { quantity = Math.max(quantity, 0) }

        // Update line item quantity and total line prices.
        const { uuid, sku, code, isUniqueLineItem } = product
        if (isUniqueLineItem) {
            // Calculate updated price.
            const price = parseFloat((product.price * quantity).toFixed(2))
            const vatPrice = parseFloat(
                (product.price * quantity * (1 + this.vatRate)).toFixed(2)
            )

            // Duplicate line item with updated details and replace.
            const detail = this.#uniqueLineItems.get(code)?.detail
            const lineItem = {
                uuid, sku, code, quantity, product, price, vatPrice, detail
            }
            this.#uniqueLineItems.set(code, lineItem)
        }
        else {
            throw new BomError({
                message: "Cannot set quantity of non-unique line item",
                fix: "Set quantity of a unique product line item"
            })
        }

        return this
    }

    /**
     * Check if product is contained under a unique line item in current bom
     * instance.
     * @param {Product} product Product to search for in bom.
     * @returns {boolean} Is product a unique line item in bom?
     */
    isUniqueLineItem(product) {
        return this.#uniqueLineItems.has(product.code) ? true : false
    }

    /**
     * Create new Bom instance by filtering current instance by a given display
     * category for rendering in quote review and calculator interface.
     * @param {ProductDisplayCategory} displayCategory Category to filter.
     * @returns {Bom} New bill of materials instance filtered by category.
     */
    filterDisplayCategory(displayCategory) {
        const bom = new Bom()

        // Filter all line items by target category and add to new bom instance.
        this.lineItems.filter(({ product, quantity, detail }) => {
            if (product.displayCategory === displayCategory) {
                bom.addLineItem(product, quantity, { detail })
            }
        })

        return bom
    }

    /**
     * Append *all* line items from an existing bom instance to this instance.
     * @param {Bom} bom Bom instance to append to current instance.
     * @returns {Bom} Current bom instance with appended line items.
     */
    append(bom) {
        // Append *all* line items from existing bom instance.
        for (const { product, quantity, detail } of bom.lineItems) {
            this.addLineItem(product, quantity, {
                forceUnique: bom.isUniqueLineItem(product),
                detail
            })
        }

        return this
    }

    /**
     * 
     * @param {MembraneType} type
     * @returns {Bom}
     */
    getMembranes(type) {
        return Bom.from(this.lineItems
            .filter(({ product }) => {
                return product instanceof Membrane && product.type === type
            })
            .sort((lineItemA, lineItemB) => {
                const { quantity: quantityA } = lineItemA
                const widthA = /** @type {number} */ (lineItemA.product.width)
                const { quantity: quantityB } = lineItemB
                const widthB = /** @type {number} */ (lineItemB.product.width)

                return widthA - widthB || quantityA - quantityB
            }))
    }

    /**
     * 
     * @param {EdgeType} type
     * @returns {Bom}
     */
    getMetalTrims(type) {
        return Bom.from(this.lineItems
            .filter(({ product }) => {
                return product instanceof Trim &&
                    product.material === "metal" &&
                    product.edgeType === type
            }))
    }

    /**
     * 
     * @param {EdgeType} type
     * @returns {Bom}
     */
    getPvcTrims(type) {
        return Bom.from(this.lineItems
            .filter(({ product }) => {
                return product instanceof Trim && product.edgeType === type
            }))
    }

    /**
     * 
     * @param {EdgeType} type
     * @param {"INT"|"EXT"} angle
     * @returns {Bom}
     */
    getPvcCorners(type, angle) {
        const cornerAngle = angle === "INT" ? 3 * Math.PI / 2
            : angle === "EXT" ? Math.PI / 2
            : NaN

        return Bom.from(this.lineItems
            .filter(({ product }) => {
                return product instanceof Corner &&
                    product.edgeType === type &&
                    product.cornerAngle === cornerAngle
            }))
    }

    /**
     * 
     * @param {TapeType} type
     * @param {object} obj
     * @param {boolean} [obj.isPatch]
     * @returns {Bom}
     */
    getTapes(type, { isPatch = false } = {}) {
        return Bom.from(this.lineItems
            .filter(({ product }) => {
                return product instanceof Tape &&
                    product.type === type &&
                    !!product.patchType === isPatch
            })
            .sort((lineItemA, lineItemB) => {
                const widthA = /** @type {number} */ (lineItemA.product.width)
                const widthB = /** @type {number} */ (lineItemB.product.width)

                return widthA - widthB
            }))
    }

    /**
     * 
     * @param {AdhesiveType} type
     * @returns {Bom}
     */
    getAdhesives(type) {
        return Bom.from(this.lineItems
            .filter(({ product }) => {
                return product instanceof Adhesive && product.type === type
            })
            .sort((lineItemA, lineItemB) => {
                const { product: { quantity: quantityA } } = lineItemA
                const { product: { quantity: quantityB } } = lineItemB

                return quantityB - quantityA
            }))
    }

    /**
     * Instantiate bill of materials from line items.
     * @param {LineItem[]} lineItems Array of line items from which to generate
     * bill of materials.
     * @param {object} obj Optional destructured parameters object.
     * @param {number} [obj.vatRate] Decimal VAT rate to apply to line items.
     * @param {boolean} [obj.allowNegativeQuantities] Should negative quantities
     * be allowed on a line item?
     * @returns {Bom} New Bom instance from line items.
     */
    static from(lineItems, {
        vatRate = 0.2,
        allowNegativeQuantities = false
    } = {}) {
        const bom = new Bom({ vatRate, allowNegativeQuantities })

        // Add all line items to new bom instance.
        for (const { product, quantity, detail } of lineItems) {
            bom.addLineItem(product, quantity, { detail })
        }

        return bom
    }

    /**
     * Instantiate bill of materials by joining existing boms.
     * @param  {Bom[]} boms Bill of materials to join together.
     * @param {object} obj Optional destructured parameters object.
     * @param {number} [obj.vatRate] Decimal VAT rate to apply to line items.
     * @param {boolean} [obj.allowNegativeQuantities] Should negative quantities
     * be allowed on a line item?
     * @returns {Bom} New Bom instance from joined existing instances.
     */
    static join(boms, {
        vatRate = 0.2,
        allowNegativeQuantities = false
    } = {}) {
        const joinedBom = new Bom({ vatRate, allowNegativeQuantities })

        // Add all line items from every bom to new joined bom instance.
        for (const bom of boms) {
            for (const { product, quantity, detail } of bom.lineItems) {
                // If joined bom already contains product, line item uniqueness
                // will be inferred from existing line item otherwise set force
                // unique flag from new line item.
                const forceUnique = joinedBom
                    .lineItems
                    .some(lineItem => lineItem.product === product)
                        ? false
                        : bom.isUniqueLineItem(product)

                // Add line item to joined bom.
                joinedBom.addLineItem(product, quantity, {
                    forceUnique,
                    detail
                })
            }
        }

        return joinedBom
    }

    /**
     * Get *all* line items in bom instance including unique and non-unique line
     * items. Ignore line items with 0 quantity, but keep line items with
     * negative quantity if found.
     */
    get lineItems() {
        return [...this.uniqueLineItems, ...this.nonUniqueLineItems]
            .filter(({ quantity }) => quantity)
    }

    /**
     * Get *all* raw line items (only properties required for saving quotes and
     * adding to basket) in bom instance including unique and non-unique line
     * items.
     */
    get rawLineItems() {
        const rawLineItems = /** @type {RawLineItem[]} */ ([])

        // Add all raw line items, exposing only properties required fo saving
        // quotes and  adding to basket.
        for (const { product, quantity, detail } of this.lineItems) {
            const { uuid, sku, price: retailPrice } = product
            rawLineItems.push({ uuid, sku, quantity, retailPrice, detail })
        }

        return rawLineItems
    }

    /**
     * Get only line items in bom instance corresponding to products which
     * should have a unique line item for the given product code.
     */
    get uniqueLineItems() {
        const uniqueLineItems = /** @type {LineItem[]} */ ([])

        // Add all unique line items to returned results.
        for (const [, lineItem] of this.#uniqueLineItems) {
            uniqueLineItems.push(lineItem)
        }

        return uniqueLineItems
    }

    /**
     * Get only line items in bom instance corresponding to products which
     * do *not* require a unique line item for the given product code, and may
     * therefore have duplicate line items per code with different quantities.
     */
    get nonUniqueLineItems() {
        return this.#nonUniqueLineItems
    }

    /**
     * Get *all* line items in bom instance, reducing any duplicate code line
     * items to a single line item with a summed quantity.
     */
    get reducedLineItems() {
        const bom = new Bom()

        // Duplicate all line items to returned bom, ensuring no duplicate line
        // items by combining quantities.
        for (const { product, quantity, detail } of this.lineItems) {
            bom.addLineItem(product, quantity, { forceUnique: true, detail })
        }

        return bom.lineItems
    }

    /**
     * 
     * @returns {CategorizedBom[]} 
     */
    get categorizedBoms() {
        return [
            {
                title: "Membranes",
                entries: [
                    { id: "one-piece", bom: this.getMembranes(MEMBRANE_TYPES.onePiece) },
                    { id: "fleece-backed", bom: this.getMembranes(MEMBRANE_TYPES.fleeceBacked) }
                ]
            },
            {
                title: "PVC Trims",
                entries: [
                    { id: "raised-kerb", bom: this.getPvcTrims(EDGE_TYPES.raisedKerb) },
                    { id: "gutter-drip", bom: this.getPvcTrims(EDGE_TYPES.gutterDrip) },
                    { id: "raised-kerb-internal", bom: this.getPvcCorners(EDGE_TYPES.raisedKerb, "INT") },
                    { id: "raised-kerb-external", bom: this.getPvcCorners(EDGE_TYPES.raisedKerb, "EXT") },
                    { id: "gutter-drip-internal", bom: this.getPvcCorners(EDGE_TYPES.gutterDrip, "INT") },
                    { id: "gutter-drip-external", bom: this.getPvcCorners(EDGE_TYPES.gutterDrip, "EXT") }
                ]
            },
            {
                title: "Metal Trims",
                entries: [
                    { id: "wall-trim", bom: this.getMetalTrims(EDGE_TYPES.houseWall) },
                    { id: "termination-bar", bom: this.getMetalTrims(EDGE_TYPES.feltJoin) }
                ]
            },
            {
                title: "Tapes",
                entries: [
                    { id: "seam-tape", bom: this.getTapes(TAPE_TYPES.seam) },
                    { id: "cured-tape", bom: this.getTapes(TAPE_TYPES.cured) },
                    { id: "uncured-tape", bom: this.getTapes(TAPE_TYPES.uncured) },
                    { id: "uncured-patch", bom: this.getTapes(TAPE_TYPES.uncured, { isPatch: true }) },
                    { id: "mechanical-tape", bom: this.getTapes(TAPE_TYPES.mechanical) }
                ]
            },
            {
                title: "Deck Adhesives",
                entries: [
                    { id: "water-based", bom: this.getAdhesives(ADHESIVE_TYPES.deck.waterBased) },
                    { id: "pu", bom: this.getAdhesives(ADHESIVE_TYPES.deck.pu) },
                    { id: "contact-bonding", bom: this.getAdhesives(ADHESIVE_TYPES.deck.contactBonding) }
                ]
            },
            {
                title: "Primers",
                entries: [
                    { id: "primer", bom: this.getAdhesives(ADHESIVE_TYPES.primer) }
                ]
            },
            {
                title: "Sealants",
                entries: [
                    { id: "lap-edge", bom: this.getAdhesives(ADHESIVE_TYPES.sealants.lapEdge) },
                    { id: "water-block", bom: this.getAdhesives(ADHESIVE_TYPES.sealants.waterBlock) },
                    { id: "ms-polymer", bom: this.getAdhesives(ADHESIVE_TYPES.sealants.msPolymer) },
                    { id: "black-silicone", bom: this.getAdhesives(ADHESIVE_TYPES.sealants.blackSilicone) }
                ]
            },
            {
                title: "Extras",
                entries: [
                    { id: "extras-tools", bom: this.filterDisplayCategory(PRODUCT_DISPLAY_CATEGORIES.extra) },
                    {
                        id: "extras-kits",
                        bom: Bom.join([
                            this.filterDisplayCategory(PRODUCT_DISPLAY_CATEGORIES.kit), // @no-wrap
                            this.filterDisplayCategory(PRODUCT_DISPLAY_CATEGORIES.drain) // @no-wrap
                        ])
                    }
                ]
            }
        ]
    }

    /**
     * Get *count* of membrane products in current bom instance.
     */
    get membraneCount() {
        let membraneCount = 0
        for (const { product } of this.lineItems) {
            if (product instanceof Membrane) { membraneCount++ }
        }

        return membraneCount
    }

    /**
     * Get total price of all line items in current bom instance.
     */
    get price() {
        const price = this.lineItems.reduce((acc, { product, quantity }) => {
            return acc + product.price * quantity
        }, 0)

        return parseFloat(price.toFixed(2))
    }

    /**
     * Get total price including VAT of all line items in current bom instance.
     */
    get vatPrice() {
        const vatPrice = this.lineItems.reduce((acc, { product, quantity }) => {
            return acc + product.price * quantity * (1 + this.vatRate)
        }, 0)

        return parseFloat(vatPrice.toFixed(2))
    }
}

/**
 * Named bom error.
 */
class BomError extends DecoratedError {
    /**
     * Construct linked list named error.
     * @param {Object.<string, string|number|boolean>} options Options object
     * containing any string keys, and string, number or boolean values.
     */
    constructor(options) {
        super(options)
        this.name ??= "BomError"
    }
}

// @@exports
export { Bom }
